<template>
  <div>
    <div class="control-header">
      <label class="input-caption">Prompt</label>
      <div class="guidance-tooltips">
        <GetStartedTooltip />
        <TipsTooltip />
      </div>
    </div>
    <q-input autogrow outlined v-model="localPrompt" type="text" placeholder="Describe what you want to change..." @input="updatePrompt" class="prompt-input" />

    <div class="control-header mt-3">
      <label class="input-caption">Aspect Ratio</label>
    </div>
    <q-select v-model="localAspectRatio" :options="aspectRatioOptions" outlined emit-value map-options @input="updateAspectRatio" option-value="value" option-label="label" class="aspect-ratio-select">
      <template slot="selected-item" slot-scope="scope">
        <div class="selected-ratio">
          <div class="ratio-icon" :class="{ 'match-input-icon': scope.opt.value === 'match_input_image' }" :style="scope.opt.value !== 'match_input_image' ? { aspectRatio: scope.opt.display } : {}">
            <q-icon v-if="scope.opt.value === 'match_input_image'" name="photo_size_select_large" size="12px" color="white" />
          </div>
          <div class="ratio-info">
            <span class="ratio-label">{{ scope.opt.label }}</span>
            <span class="ratio-description">{{ scope.opt.description }}</span>
          </div>
        </div>
      </template>
      <template slot="option" slot-scope="scope">
        <q-item v-bind="scope.itemProps" @click="scope.itemEvents.click" clickable>
          <q-item-section avatar>
            <div class="ratio-icon" :class="{ 'match-input-icon': scope.opt.value === 'match_input_image' }" :style="scope.opt.value !== 'match_input_image' ? { aspectRatio: scope.opt.display } : {}">
              <q-icon v-if="scope.opt.value === 'match_input_image'" name="photo_size_select_large" size="12px" color="white" />
            </div>
          </q-item-section>
          <q-item-section>
            <q-item-label>{{ scope.opt.label }}</q-item-label>
            <q-item-label caption>{{ scope.opt.description }}</q-item-label>
          </q-item-section>
        </q-item>
      </template>
    </q-select>

    <q-btn class="action-button action-button--primary mt-3" :loading="isGenerating" @click="generateEdit" :disable="!localPrompt.trim()" flat no-caps>
      <template v-if="!isGenerating">
        <q-icon name="auto_fix_high" size="xs" class="button-icon" color="grey-7" />
        Generate
      </template>
      <template v-slot:loading>
        <q-spinner color="grey-7" class="on-left" />
        <span v-if="operationStatus === 'preparing'">AI At Work… <q-icon name="fas fa-magic" size="xs" color="grey-7" /></span>
        <span v-else-if="operationStatus === 'starting'">Working Our Magic! <q-icon name="fas fa-hat-wizard" size="xs" color="grey-7" /></span>
        <span v-else-if="operationStatus === 'processing'">Almost Ready... <q-icon name="fas fa-hourglass-half" size="xs" color="grey-7" /></span>
      </template>
    </q-btn>
  </div>
</template>

<script>
import GetStartedTooltip from '../GetStartedTooltip.vue';
import TipsTooltip from '../TipsTooltip.vue';

export default {
  name: 'TextBasedEditControls',
  components: {
    GetStartedTooltip,
    TipsTooltip
  },
  props: {
    isGenerating: {
      type: Boolean,
      required: true
    },
    operationStatus: {
      type: String,
      default: null
    },
    prompt: {
      type: String,
      default: ''
    },
    aspectRatio: {
      type: String,
      default: 'match_input_image'
    }
  },
  data() {
    return {
      localPrompt: this.prompt,
      localAspectRatio: this.aspectRatio,
      aspectRatioOptions: [
        {
          value: 'match_input_image',
          label: 'Match Input Image',
          display: '1/1',
          description: 'Keep original dimensions'
        },
        {
          value: '1:1',
          label: '1:1 Square',
          display: '1/1',
          description: 'Instagram posts'
        },
        {
          value: '16:9',
          label: '16:9 Landscape',
          display: '16/9',
          description: 'YouTube, Facebook covers'
        },
        {
          value: '9:16',
          label: '9:16 Portrait',
          display: '9/16',
          description: 'Stories, Reels, TikTok'
        },
        {
          value: '4:3',
          label: '4:3 Classic',
          display: '4/3',
          description: 'Traditional photos'
        },
        {
          value: '3:4',
          label: '3:4 Portrait',
          display: '3/4',
          description: 'Instagram portrait posts'
        }
      ]
    };
  },
  mounted() {
    // Component mounted
  },
  watch: {
    prompt(newVal) {
      this.localPrompt = newVal;
    },
    aspectRatio(newVal) {
      this.localAspectRatio = newVal;
    },
    localAspectRatio(newVal) {
      // Local aspect ratio changed
    }
  },
  methods: {
    updatePrompt() {
      this.$emit('update:prompt', this.localPrompt);
    },
    updateAspectRatio(value) {
      this.localAspectRatio = value;
      this.$emit('update:aspectRatio', value);
    },
    generateEdit() {
      this.$emit('generateEdit');
    }
  }
};
</script>

<style lang="scss" scoped>
.control-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  min-height: 24px;
}

.input-caption {
  @include mona-sans-font($size: 15px, $bolded: true, $color: var(--main-text-color));
  margin-bottom: 0;
  display: block;
}

.prompt-input {
  margin-bottom: 8px;
}

.aspect-ratio-select {
  margin-bottom: 8px;
}

.selected-ratio {
  display: flex;
  align-items: center;
  gap: 12px;
}

.ratio-icon {
  width: 20px;
  height: 20px;
  background: #fecb2f;
  border-radius: 2px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  &.match-input-icon {
    background: #6366f1;
  }
}

.ratio-info {
  display: flex;
  flex-direction: column;
}

.ratio-label {
  @include mona-sans-font($size: 14px, $bolded: true, $color: var(--main-text-color));
  line-height: 1.2;
}

.ratio-description {
  @include inter-font($size: 12px, $color: var(--secondary-text-color));
  line-height: 1.2;
}

.action-button {
  width: 100%;
  padding: 12px 16px;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  @include mona-sans-font($size: 15px, $bolded: true, $color: var(--main-text-color));
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &--primary {
    background: #fecb2f;
    color: #333333;

    &:hover {
      background: #fed95f;
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.help-icon {
  color: #9ca3af;
  cursor: pointer;
}

.button-icon {
  margin-right: 8px;
  font-size: 16px;
}

.mt-3 {
  margin-top: 16px;
}

.usage-hint {
  margin-top: 16px;
  padding: 12px;
  background-color: #f9fafb;
  border-radius: 8px;
  display: flex;
  align-items: center;

  .info-icon {
    margin-right: 10px;
    flex-shrink: 0;
  }

  p {
    @include inter-font($size: 14px, $color: var(--secondary-text-color));
    margin: 0;
    line-height: 1.5;
  }
}

.guidance-tooltips {
  display: flex;
  gap: 8px;
  align-items: center;
}
</style>
